import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBible } from '../context/BibleContext';
import { useBookmarks } from '../hooks/annotations';
import { useBooksQuery } from '../hooks/queries';
import type { Bookmark } from '../types';

interface BookmarksMenuProps {
  // Called before jumping to a bookmark, so search results make way for the reader.
  onNavigate: () => void;
}

export default function BookmarksMenu({ onNavigate }: BookmarksMenuProps) {
  const { bookmarks, removeBookmark, isSavedToAccount } = useBookmarks();
  const { data: books = [] } = useBooksQuery();
  const { openBibles, updateOpenBible } = useBible();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  const reference = (bookmark: Bookmark) =>
    `${books.find(b => b.id === bookmark.canonicalBookId)?.name ?? '…'} ${bookmark.chapterNumber}:${bookmark.verseNumber}`;

  // Opens the verse in the first panel, in whichever translation that panel is showing.
  const handleOpenBookmark = (bookmark: Bookmark) => {
    const panel = openBibles[0];
    if (!panel) return;
    onNavigate();
    updateOpenBible(panel.id, {
      canonicalBookId: bookmark.canonicalBookId,
      selectedChapter: bookmark.chapterNumber,
      targetVerse: bookmark.verseNumber,
    });
    setOpen(false);
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        title="Bookmarks"
        aria-label="Bookmarks"
        aria-expanded={open}
        className="p-2 rounded-lg transition-colors text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-parchment-100 dark:hover:bg-ink-700"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill={bookmarks.length > 0 ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-lg shadow-lg py-1 z-50 bg-white border border-gray-200 dark:bg-ink-700 dark:border-ink-600">
          <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider border-b text-gray-500 border-gray-100 dark:text-gray-400 dark:border-ink-600">
            Bookmarks
          </div>

          {bookmarks.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
              No bookmarks yet. Use the bookmark icon next to a verse.
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {bookmarks.map(bookmark => (
                <li key={bookmark.id} className="flex items-center group">
                  <button
                    onClick={() => handleOpenBookmark(bookmark)}
                    className="flex-1 min-w-0 text-left px-3 py-2 text-sm truncate transition-colors text-gray-700 hover:bg-gray-50 dark:text-parchment-100 dark:hover:bg-ink-600"
                  >
                    {reference(bookmark)}
                  </button>
                  <button
                    onClick={() => removeBookmark(bookmark)}
                    title="Remove bookmark"
                    aria-label={`Remove bookmark ${reference(bookmark)}`}
                    className="px-3 py-2 transition-colors text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!isSavedToAccount && (
            <div className="px-3 py-2 text-xs border-t text-gray-500 border-gray-100 dark:text-gray-400 dark:border-ink-600">
              Your bookmarks and highlights are lost when the page is reloaded.{' '}
              <Link to="/login" className="text-blue-600 hover:underline dark:text-blue-400">Log in</Link> to keep them.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
